import { useMemo, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import {
  GoalsNudgeData,
  GoalsNudgeDataProps,
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
  NudgeAction,
  NudgeCardItem,
} from '@mesh.ai/rabbit';
import { useHistory } from 'react-router-dom';

import { handleActionClick, openInSameTab } from '../helpers/nudgeActionHelpers';
import { UITemplate } from '../types/nudgeCard';

type Props = GoalsNudgeDataProps & {
  nudgeCard: {
    ecaId: number;
    uiTemplate: UITemplate;
  };
  buttonLink: string;
  buttonText: string;
  startingValue: string;
  actual: string;
  target: string;
};

function isTextTruncated(element) {
  // Compare the scroll width with the client width
  return element?.scrollWidth > element?.clientWidth;
}

const GoalCheckinPendingNudge = ({ buttonLink, buttonText, nudgeCard, ...nc }: Props) => {
  const history = useHistory();
  const elRef = useRef(null);
  const truncatedText = elRef.current?.querySelector('.truncate');
  const isTruncated = useMemo(() => truncatedText && isTextTruncated(truncatedText), [truncatedText]);

  const { target = '0', startingValue = '0', actual = '0' } = nc ?? {};

  // Copied from src/components/RabbitOKRSidePanel/PrimaryPanel/Sections/CheckIn/CheckIn.jsx
  // Backend percentage calculation logic, don't change this
  const computedProgressPercentage = useMemo(() => {
    const floatStartingValue = parseFloat(startingValue);
    const floatTarget = parseFloat(target);
    const floatActual = parseFloat(actual);

    const denominator = floatTarget - floatStartingValue;
    if (floatStartingValue === floatTarget && floatStartingValue !== 0 && floatTarget !== 0) {
      const deviationPercentage = ((floatActual - floatStartingValue) / floatTarget) * 100;
      return Math.min(100, Math.max(0, 100 - Math.abs(deviationPercentage)));
    }
    return denominator === 0 ? 0 : ((floatActual - floatStartingValue) / (floatTarget - floatStartingValue)) * 100;
  }, [actual, startingValue, target]);

  return (
    <Box w="100%">
      <HoverCard openDelay={300}>
        <HoverCardTrigger>
          <NudgeCardItem>
            <GoalsNudgeData ref={elRef} {...(nc as GoalsNudgeDataProps)} achieved={computedProgressPercentage} />
            <NudgeAction
              onClick={() =>
                handleActionClick({
                  ecaId: nudgeCard.ecaId,
                  uiTemplate: nudgeCard.uiTemplate,
                  action: () => openInSameTab(history, buttonLink),
                })
              }
            >
              {buttonText}
            </NudgeAction>
          </NudgeCardItem>
        </HoverCardTrigger>
        <HoverCardPortal>
          {isTruncated && (
            <HoverCardContent side="top" className="HoverCardContent">
              <GoalsNudgeData
                {...(nc as GoalsNudgeDataProps)}
                achieved={computedProgressPercentage}
                truncateExplanation={false}
              />
            </HoverCardContent>
          )}
        </HoverCardPortal>
      </HoverCard>
    </Box>
  );
};

export { GoalCheckinPendingNudge };
